import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useI18n } from '@/i18n';

const TeachFAQSection: React.FC = () => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const faqs = [
    {
      question: isZh ? '注册成为导师是免费的吗？' : 'Is it free to sign up as a tutor?',
      answer: isZh
        ? '是的。在 Tutor Founder 注册并建立导师档案完全免费，没有注册费、月费或前期成本。你只需要花时间完善自己的资料。'
        : 'Yes — signing up and creating your tutor profile on Tutor Founder is completely free. There are no registration fees, no monthly subscriptions, and no upfront costs. You only need to invest your time in completing your profile.',
    },
    {
      question: isZh ? '审核时需要提交哪些文件？' : 'What documents do I need to submit for verification?',
      answer: isZh
        ? '你需要提交有效的政府签发照片身份证明（身份证、护照或驾照）。若你有正式教学资格或相关证书，上传后会加强你的档案并提升配对机会。具教师背景的申请者可能会被要求补交额外支持文件。'
        : 'You will need to submit a valid government-issued photo ID (MyKad, passport, or driving licence). If you have formal teaching qualifications or relevant certificates, uploading them will strengthen your profile and improve your chances of being matched. Teacher-related applicants may be asked for an additional support document.',
    },
    {
      question: isZh ? '加入平台一定要有正式教学资格吗？' : 'Do I need a formal teaching qualification to join?',
      answer: isZh
        ? '不一定。我们欢迎大学毕业生、在读大学生，以及具备扎实学科能力的专业人士加入。最重要的是你是否能清楚讲解概念，并认真对待学生。你的学历背景与相关经验会在审核过程中核实。'
        : 'No formal teaching certificate is required. We welcome university graduates, undergraduates, and professionals with strong subject knowledge. What matters most is your ability to explain concepts clearly and your commitment to students. Your educational background and relevant experience are verified during the approval process.',
    },
    {
      question: isZh ? '付款如何处理？我什么时候会收到钱？' : 'How does payment work? When will I get paid?',
      answer: isZh
        ? '家长在预约课程时会向 Tutor Founder 支付款项，导师则会在每月底收到拨款。这种安排能保障双方权益；如果课程被取消，家长有保障，而导师也能获得稳定整合的月度付款。'
        : 'Parents pay tuition fees to Tutor Founder when they book sessions. Tutors receive their payouts at the end of each month. This structure protects both parties — parents are covered if a session is cancelled, and tutors receive reliable, consolidated monthly payments.',
    },
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white py-14 md:py-20">
      <div className="container mx-auto max-w-6xl px-4 md:px-8 lg:px-12 xl:px-16">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-8 text-left md:mb-12"
        >
          <h2 className="mb-3 max-w-xl text-4xl font-bold tracking-tight text-gray-900 md:text-5xl">
            {isZh ? '你可能想知道' : 'You may want to know'}
          </h2>
        </motion.div>

        <motion.div
          className="max-w-4xl space-y-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.04)]"
            >
              <button
                onClick={() => toggle(index)}
                className="flex w-full cursor-pointer items-center justify-between p-5 text-left transition-colors duration-200 hover:bg-gray-50"
              >
                <span className="pr-8 font-semibold text-gray-900">
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="h-5 w-5 flex-shrink-0 text-primary" />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <motion.div
                      initial={{ y: -8 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-gray-200 px-5 pb-5 pt-4 text-sm leading-relaxed text-gray-600"
                    >
                      {faq.answer}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TeachFAQSection;
